type IndexableValue<T, K extends keyof T> = T[K] & (number | string | boolean | null | undefined);

/**
 * Indexes values by key returning a mapping of value to array of entities whose
 * key resolves to that value
 * @param coll entity collection to index
 * @param k key to look up index value
 * @example
 * const objs = [
 *     {id: 2, display_name: 'value'},
 *     {id: 1, display_name: 'value'},
 *     {id: 3, display_name: 'other value'}
 * ]
 * const objsByDisplayName = indexByPrimaryKey(objs, 'display_name');
 * {
 *     'value': [{id: 2, other: 'value'}, {id: 1, other: 'value'}],
 *     'other value': [{id: 3, other: 'other value'}]
 * } === objsByDisplayName
 */
export const indexByKey = <
  T extends { [key: string]: any },
  K extends keyof T,
  V extends IndexableValue<T, K>,
>(
  coll: T[],
  k: K,
): Partial<Record<`${V}`, T[]>> => {
  const byKey: any = {};
  coll.forEach((x) => {
    const index = x[k];
    if (!(index in byKey)) {
      byKey[index] = [x];
    } else {
      byKey[index].push(x);
    }
  });
  return byKey;
};

/**
 * Indexes values by primary key returning a mapping of value to last seen entity matching that key
 * @param coll entity collection to index
 * @param k key to look up index value
 * @example
 * const objsById = indexByPrimaryKey([{id: 2, other: 'value'}, {id: 1, other: 'value'}], 'id');
 * {
 *     2: {id: 2, other: 'value'},
 *     1: {id: 1, other: 'value'}
 * } === objsById
 */
export const indexByPrimaryKey = <
  T extends { [key: string]: any },
  K extends keyof T & (string | number | boolean),
  V extends IndexableValue<T, K>,
>(
  coll: T[],
  k: K,
): Partial<Record<V, T>> => {
  return Object.fromEntries(
    Object.entries(indexByKey(coll, k)).map(([index, values]) => [
      index,
      (values as T[])[(values as T[]).length - 1],
    ]),
  ) as any;
};

/**
 * Dedupe collection of `T` by a field, preserving ordering.
 * The first or last instance with a given value for `k` is kept, depending on the 'keep' value.
 */
export const dedupeByKey = <T extends { [key: string]: any }, K extends keyof T>(
  coll: T[],
  k: K,
  keep: "first" | "last" = "first",
): T[] => {
  const deduped: T[] = [];
  const seen = new Set();
  const collCopy = [...coll];
  if (keep === "last") {
    collCopy.reverse();
  }
  collCopy.forEach((x) => {
    const v = x[k];
    if (!seen.has(v)) {
      deduped.push(x);
      seen.add(v);
    }
  });
  if (keep === "last") {
    deduped.reverse();
  }
  return deduped;
};
